import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Phone, Send, MessageSquare, Calendar, Clock, Users, Building2, 
  Loader2, CheckCircle, XCircle, AlertTriangle, RefreshCw, Play,
  ChevronDown, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export default function AdminWhatsApp() {
  var [status, setStatus] = useState(null);
  var [automationStatus, setAutomationStatus] = useState(null);
  var [schedulerLogs, setSchedulerLogs] = useState([]);
  var [messages, setMessages] = useState([]);
  var [appointments, setAppointments] = useState([]);
  var [loading, setLoading] = useState(true);
  var [sending, setSending] = useState(false);
  var [testPhone, setTestPhone] = useState('');
  var [customMessage, setCustomMessage] = useState({ phone: '', message: '' });
  var [activeSubTab, setActiveSubTab] = useState('dashboard');
  var [sendingReminders, setSendingReminders] = useState(false);
  var [triggeringJob, setTriggeringJob] = useState(false);
  
  // Campaign states
  var [contacts, setContacts] = useState({ centers: [], professionals: [], workshops: [], summary: { centers: 0, professionals: 0, workshops: 0, total: 0 } });
  var [campaigns, setCampaigns] = useState([]);
  var [selectedGroup, setSelectedGroup] = useState('centers');
  var [selectedContacts, setSelectedContacts] = useState([]);
  var [campaignMessage, setCampaignMessage] = useState('');
  var [campaignName, setCampaignName] = useState('');
  var [sendingCampaign, setSendingCampaign] = useState(false);
  var [loadingContacts, setLoadingContacts] = useState(false);

  useEffect(function() {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      var [statusRes, automationRes, messagesRes, appointmentsRes, logsRes, campaignsRes] = await Promise.all([
        authAxios.get(API + '/whatsapp/status', { withCredentials: true }),
        authAxios.get(API + '/whatsapp/automation-status', { withCredentials: true }).catch(function() { return { data: {} }; }),
        authAxios.get(API + '/admin/whatsapp/messages?limit=50', { withCredentials: true }).catch(function() { return { data: { messages: [] } }; }),
        authAxios.get(API + '/admin/appointments', { withCredentials: true }).catch(function() { return { data: { appointments: [] } }; }),
        authAxios.get(API + '/whatsapp/scheduler-logs?limit=10', { withCredentials: true }).catch(function() { return { data: { logs: [] } }; }),
        authAxios.get(API + '/whatsapp/campaigns?limit=10', { withCredentials: true }).catch(function() { return { data: { campaigns: [] } }; })
      ]);
      setStatus(statusRes.data);
      setAutomationStatus(automationRes.data);
      setMessages(messagesRes.data.messages || []);
      setAppointments(appointmentsRes.data.appointments || []);
      setSchedulerLogs(logsRes.data.logs || []);
      setCampaigns(campaignsRes.data.campaigns || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function loadContacts() {
    setLoadingContacts(true);
    try {
      var res = await authAxios.get(API + '/whatsapp/contacts?group=all', { withCredentials: true });
      setContacts(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando contactos');
    }
    setLoadingContacts(false);
  }

  async function sendCampaign() {
    if (!campaignMessage.trim()) {
      toast.error('Escribe un mensaje para la campaña');
      return;
    }
    if (selectedContacts.length === 0) {
      toast.error('Selecciona al menos un contacto');
      return;
    }
    setSendingCampaign(true);
    try {
      var res = await authAxios.post(API + '/whatsapp/campaign', {
        name: campaignName || 'Campaña ' + new Date().toLocaleDateString(),
        message: campaignMessage,
        contacts: selectedContacts,
        group: selectedGroup
      }, { withCredentials: true });
      toast.success('Campaña enviada: ' + res.data.sent + ' mensajes exitosos');
      setCampaignMessage('');
      setCampaignName('');
      setSelectedContacts([]);
      loadData();
    } catch (e) {
      toast.error('Error enviando campaña');
    }
    setSendingCampaign(false);
  }

  async function sendTestMessage() {
    if (!testPhone) {
      toast.error('Ingresa un número de teléfono');
      return;
    }
    setSending(true);
    try {
      await authAxios.post(API + '/whatsapp/test', { phone: testPhone }, { withCredentials: true });
      toast.success('Mensaje de prueba enviado');
      setTestPhone('');
      loadData();
    } catch (e) {
      toast.error('Error enviando mensaje de prueba');
    }
    setSending(false);
  }

  async function sendCustomMessage() {
    if (!customMessage.phone || !customMessage.message) {
      toast.error('Completa teléfono y mensaje');
      return;
    }
    setSending(true);
    try {
      await authAxios.post(API + '/whatsapp/send', customMessage, { withCredentials: true });
      toast.success('Mensaje enviado');
      setCustomMessage({ phone: '', message: '' });
      loadData();
    } catch (e) {
      toast.error('Error enviando mensaje');
    }
    setSending(false);
  }

  async function sendPendingReminders() {
    setSendingReminders(true);
    try {
      var res = await authAxios.post(API + '/whatsapp/send-pending-reminders', {}, { withCredentials: true });
      toast.success('Recordatorios enviados: ' + res.data.sent + ' de ' + res.data.total);
      loadData();
    } catch (e) {
      toast.error('Error enviando recordatorios');
    }
    setSendingReminders(false);
  }

  async function triggerSchedulerJob() {
    setTriggeringJob(true);
    try {
      await authAxios.post(API + '/admin/run-job?job=whatsapp_reminders', {}, { withCredentials: true });
      toast.success('Job ejecutado correctamente');
      loadData();
    } catch (e) {
      toast.error('Error ejecutando job');
    }
    setTriggeringJob(false);
  }

  function toggleContactSelection(contact) {
    var phone = contact.phone || contact.whatsapp;
    if (!phone) return;
    
    if (selectedContacts.find(function(c) { return c.phone === phone; })) {
      setSelectedContacts(selectedContacts.filter(function(c) { return c.phone !== phone; }));
    } else {
      setSelectedContacts([...selectedContacts, { 
        phone: phone, 
        name: contact.name || contact.center_name || contact.professional_name || 'Sin nombre',
        entity_type: selectedGroup.slice(0, -1),
        entity_id: contact.center_id || contact.user_id || contact.workshop_id
      }]);
    }
  }

  function selectAllContacts() {
    var currentContacts = contacts[selectedGroup] || [];
    var validContacts = currentContacts.filter(function(c) { return c.phone || c.whatsapp; });
    
    if (selectedContacts.length === validContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(validContacts.map(function(c) {
        return {
          phone: c.phone || c.whatsapp,
          name: c.name || c.center_name || c.professional_name || 'Sin nombre',
          entity_type: selectedGroup.slice(0, -1),
          entity_id: c.center_id || c.user_id || c.workshop_id
        };
      }));
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub Navigation */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: Phone },
          { id: 'messages', label: 'Mensajes Enviados', icon: MessageSquare },
          { id: 'appointments', label: 'Citas', icon: Calendar },
          { id: 'campaigns', label: 'Campañas', icon: Users },
          { id: 'automation', label: 'Automatización', icon: Clock }
        ].map(function(tab) {
          return (
            <Button
              key={tab.id}
              variant={activeSubTab === tab.id ? 'default' : 'outline'}
              size="sm"
              onClick={function() { setActiveSubTab(tab.id); if (tab.id === 'campaigns' && contacts.centers.length === 0) loadContacts(); }}
              className={activeSubTab === tab.id ? 'bg-emerald-600' : 'border-slate-700 text-slate-300'}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          );
        })}
      </div>

      {/* Dashboard Tab */}
      {activeSubTab === 'dashboard' && (
        <div className="grid gap-6">
          {/* Status Card */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-slate-50 flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Estado de WhatsApp
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                {status?.configured ? (
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                <span className="text-slate-300">
                  {status?.configured ? 'WhatsApp configurado correctamente' : 'WhatsApp no configurado'}
                </span>
              </div>
              {status?.phone_number && (
                <p className="text-slate-400 text-sm">Número: {status.phone_number}</p>
              )}
              
              {/* Test Message */}
              <div className="pt-4 border-t border-slate-700">
                <p className="text-slate-300 text-sm mb-2">Enviar mensaje de prueba:</p>
                <div className="flex gap-2">
                  <Input 
                    placeholder="+56912345678" 
                    value={testPhone}
                    onChange={function(e) { setTestPhone(e.target.value); }}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                  <Button onClick={sendTestMessage} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Custom Message */}
              <div className="pt-4 border-t border-slate-700">
                <p className="text-slate-300 text-sm mb-2">Enviar mensaje personalizado:</p>
                <div className="space-y-2">
                  <Input 
                    placeholder="Teléfono (+56912345678)" 
                    value={customMessage.phone}
                    onChange={function(e) { setCustomMessage({...customMessage, phone: e.target.value}); }}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                  <Input 
                    placeholder="Mensaje" 
                    value={customMessage.message}
                    onChange={function(e) { setCustomMessage({...customMessage, message: e.target.value}); }}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                  <Button onClick={sendCustomMessage} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 w-full">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Enviar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-slate-50">Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button 
                onClick={sendPendingReminders} 
                disabled={sendingReminders}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                Enviar Recordatorios Pendientes
              </Button>
              <Button 
                onClick={triggerSchedulerJob}
                disabled={triggeringJob}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                {triggeringJob ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                Ejecutar Job de Recordatorios
              </Button>
              <Button 
                onClick={loadData}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar Datos
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Messages Tab */}
      {activeSubTab === 'messages' && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-slate-50 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Últimos Mensajes Enviados ({messages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No hay mensajes registrados</p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {messages.map(function(msg, idx) {
                  return (
                    <div key={idx} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-slate-300 font-medium">{msg.to_phone}</span>
                        <span className={`text-xs px-2 py-1 rounded ${msg.status === 'sent' ? 'bg-emerald-500/20 text-emerald-400' : msg.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {msg.status}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm line-clamp-2">{msg.message}</p>
                      <p className="text-slate-500 text-xs mt-2">
                        {new Date(msg.created_at).toLocaleString('es-CL')}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Appointments Tab */}
      {activeSubTab === 'appointments' && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-slate-50 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Citas Programadas ({appointments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {appointments.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No hay citas programadas</p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {appointments.map(function(apt, idx) {
                  return (
                    <div key={idx} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-slate-300 font-medium">{apt.patient_name}</p>
                          <p className="text-slate-400 text-sm">{apt.center_name || apt.professional_name}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${apt.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' : apt.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {apt.status}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-2 text-sm text-slate-400">
                        <span>{new Date(apt.appointment_date).toLocaleDateString('es-CL')}</span>
                        <span>{apt.appointment_time}</span>
                      </div>
                      {apt.reminder_sent && (
                        <p className="text-emerald-400 text-xs mt-2 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Recordatorio enviado
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Campaigns Tab */}
      {activeSubTab === 'campaigns' && (
        <div className="grid gap-6">
          {/* Create Campaign */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-slate-50">Nueva Campaña</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input 
                placeholder="Nombre de la campaña (opcional)"
                value={campaignName}
                onChange={function(e) { setCampaignName(e.target.value); }}
                className="bg-slate-800 border-slate-700 text-white"
              />
              
              {/* Contact Group Selector */}
              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="border-slate-700 text-slate-300">
                      {selectedGroup === 'centers' && <Building2 className="w-4 h-4 mr-2" />}
                      {selectedGroup === 'professionals' && <Users className="w-4 h-4 mr-2" />}
                      {selectedGroup === 'workshops' && <Briefcase className="w-4 h-4 mr-2" />}
                      {selectedGroup === 'centers' ? 'Centros' : selectedGroup === 'professionals' ? 'Profesionales' : 'Talleres'}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-slate-800 border-slate-700">
                    <DropdownMenuItem onClick={function() { setSelectedGroup('centers'); setSelectedContacts([]); }} className="text-slate-300 hover:bg-slate-700">
                      <Building2 className="w-4 h-4 mr-2" /> Centros ({contacts.summary?.centers || 0})
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={function() { setSelectedGroup('professionals'); setSelectedContacts([]); }} className="text-slate-300 hover:bg-slate-700">
                      <Users className="w-4 h-4 mr-2" /> Profesionales ({contacts.summary?.professionals || 0})
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={function() { setSelectedGroup('workshops'); setSelectedContacts([]); }} className="text-slate-300 hover:bg-slate-700">
                      <Briefcase className="w-4 h-4 mr-2" /> Talleres ({contacts.summary?.workshops || 0})
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <Button 
                  variant="outline" 
                  onClick={selectAllContacts}
                  className="border-slate-700 text-slate-300"
                >
                  {selectedContacts.length === (contacts[selectedGroup] || []).filter(function(c) { return c.phone || c.whatsapp; }).length 
                    ? 'Deseleccionar Todos' 
                    : 'Seleccionar Todos'}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={loadContacts}
                  disabled={loadingContacts}
                  className="border-slate-700 text-slate-300"
                >
                  {loadingContacts ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>

              {/* Contact List */}
              <div className="max-h-48 overflow-y-auto border border-slate-700 rounded-lg p-2">
                {(contacts[selectedGroup] || []).length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No hay contactos con WhatsApp</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {(contacts[selectedGroup] || []).map(function(contact, idx) {
                      var phone = contact.phone || contact.whatsapp;
                      var isSelected = selectedContacts.find(function(c) { return c.phone === phone; });
                      return (
                        <div
                          key={idx}
                          onClick={function() { toggleContactSelection(contact); }}
                          className={`p-2 rounded cursor-pointer text-sm ${isSelected ? 'bg-emerald-600/20 border border-emerald-500' : 'bg-slate-800 hover:bg-slate-700'} ${!phone ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <p className="text-slate-300 font-medium truncate">
                            {contact.name || contact.center_name || contact.professional_name}
                          </p>
                          <p className="text-slate-500 text-xs">{phone || 'Sin WhatsApp'}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <p className="text-slate-400 text-sm">{selectedContacts.length} contactos seleccionados</p>

              <textarea
                placeholder="Mensaje de la campaña..."
                value={campaignMessage}
                onChange={function(e) { setCampaignMessage(e.target.value); }}
                className="w-full h-32 p-3 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
              />
              
              <Button 
                onClick={sendCampaign}
                disabled={sendingCampaign || selectedContacts.length === 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {sendingCampaign ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Enviar Campaña a {selectedContacts.length} contactos
              </Button>
            </CardContent>
          </Card>

          {/* Campaign History */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-slate-50">Historial de Campañas</CardTitle>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No hay campañas registradas</p>
              ) : (
                <div className="space-y-3">
                  {campaigns.map(function(campaign, idx) {
                    return (
                      <div key={idx} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                        <div className="flex justify-between items-start">
                          <p className="text-slate-300 font-medium">{campaign.name}</p>
                          <span className={`text-xs px-2 py-1 rounded ${campaign.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {campaign.sent}/{campaign.total} enviados
                          </span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1">
                          {new Date(campaign.created_at).toLocaleString('es-CL')}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Automation Tab */}
      {activeSubTab === 'automation' && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-slate-50 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Estado de Automatización
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Automation Status */}
            <div className="grid gap-4">
              <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                {automationStatus?.reminders_enabled ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                )}
                <div>
                  <p className="text-slate-300">Recordatorios Automáticos</p>
                  <p className="text-slate-500 text-sm">
                    {automationStatus?.reminders_enabled ? 'Activo - Cada 5 minutos' : 'Configurar Twilio'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                <Clock className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-slate-300">Próxima Ejecución</p>
                  <p className="text-slate-500 text-sm">
                    {automationStatus?.next_run ? new Date(automationStatus.next_run).toLocaleString('es-CL') : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Scheduler Logs */}
            <div>
              <h4 className="text-slate-300 font-medium mb-3">Últimas Ejecuciones</h4>
              {schedulerLogs.length === 0 ? (
                <p className="text-slate-400 text-center py-4">No hay logs registrados</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {schedulerLogs.map(function(log, idx) {
                    return (
                      <div key={idx} className="p-2 bg-slate-800/50 rounded text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-300">{log.action}</span>
                          <span className={log.status === 'success' ? 'text-emerald-400' : 'text-red-400'}>{log.status}</span>
                        </div>
                        <p className="text-slate-500 text-xs">{new Date(log.created_at).toLocaleString('es-CL')}</p>
                        {log.details && <p className="text-slate-400 text-xs mt-1">{log.details}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
